import React, { useState, useEffect } from "react";
import { api } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const DeductionsLibrary = () => {
  const [deductions, setDeductions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBusinessType, setSelectedBusinessType] = useState("general");
  const [selectedDeduction, setSelectedDeduction] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchDeductions();
    fetchCategories();
  }, [selectedBusinessType]);

  const fetchInitialData = async () => {
    try {
      const [deductionsRes, categoriesRes, typesRes] = await Promise.all([
        api.get("/deductions?include_rules=true"),
        api.get("/deductions/categories"),
        api.get("/deductions/business-types"),
      ]);
      setDeductions(deductionsRes.data);
      setCategories(categoriesRes.data);
      setBusinessTypes(typesRes.data);
    } catch (error) {
      console.error("Failed to fetch deductions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeductions = async () => {
    try {
      const params = selectedBusinessType !== "general" 
        ? `?business_type=${selectedBusinessType}&include_rules=true` 
        : "?include_rules=true";
      const response = await api.get(`/deductions${params}`);
      setDeductions(response.data);
    } catch (error) {
      console.error("Failed to fetch deductions:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const params = selectedBusinessType !== "general" ? `?business_type=${selectedBusinessType}` : "";
      const response = await api.get(`/deductions/categories${params}`);
      setCategories(response.data);
      setSelectedCategory("all");
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const filteredDeductions = deductions.filter(d => {
    const matchesSearch = !search || 
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.description.toLowerCase().includes(search.toLowerCase()) ||
      (d.irs_rules && d.irs_rules.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = selectedCategory === "all" || d.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedDeductions = filteredDeductions.reduce((acc, d) => {
    if (!acc[d.category]) {
      acc[d.category] = [];
    }
    acc[d.category].push(d);
    return acc;
  }, {});

  const sortedCategories = Object.keys(groupedDeductions).sort();

  const selectedTypeInfo = businessTypes.find(t => t.id === selectedBusinessType);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading deductions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="deductions-page">
      <div>
        <h1 className="text-3xl font-bold text-stone-900">Business Deductions Library</h1>
        <p className="text-stone-600">Browse tax deductions with IRS rules filtered by your business type</p>
      </div>

      {/* Business Type Selection */}
      <Card className="bg-gradient-to-br from-[#1e3a5f]/5 to-stone-50 border-[#1e3a5f]/20">
        <CardHeader>
          <CardTitle className="text-[#1e3a5f]">Select Your Business Type</CardTitle>
          <CardDescription>
            Get personalized deduction recommendations based on your industry
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedBusinessType} onValueChange={setSelectedBusinessType} className="w-full">
            <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0">
              {businessTypes.map(type => (
                <TabsTrigger
                  key={type.id}
                  value={type.id}
                  className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white px-4 py-2 rounded-full border border-stone-200 data-[state=active]:border-[#1e3a5f]"
                  data-testid={`business-type-${type.id}`}
                >
                  {type.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {selectedTypeInfo && (
            <div className="mt-4 p-4 rounded-lg bg-white border border-[#1e3a5f]/15">
              <p className="text-sm text-stone-600">
                <span className="font-medium text-stone-900">{selectedTypeInfo.name}:</span>{" "}
                {selectedTypeInfo.description}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-4xl font-bold text-[#1e3a5f]" data-testid="total-deductions">
                {deductions.length}
              </p>
              <p className="text-sm text-stone-600">
                {selectedBusinessType === "general" ? "Total Deductions" : "Relevant Deductions"}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-4xl font-bold text-[#1e3a5f]" data-testid="total-categories">
                {categories.length}
              </p>
              <p className="text-sm text-stone-600">Categories</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-4xl font-bold text-[#1e3a5f]" data-testid="filtered-count">
                {filteredDeductions.length}
              </p>
              <p className="text-sm text-stone-600">Matching Results</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
          <CardDescription>Narrow down deductions by name, description, or IRS rules</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Search</Label>
              <Input
                type="text"
                placeholder="Search deductions, rules, forms..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-12"
                data-testid="search-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="h-12" data-testid="category-select">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="all">All Categories ({deductions.length})</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {cat} ({deductions.filter(d => d.category === cat).length})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deductions List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedBusinessType === "general" 
              ? "All Business Deductions" 
              : `Deductions for ${selectedTypeInfo?.name || selectedBusinessType}`}
          </CardTitle>
          <CardDescription>
            Click on a deduction to see detailed IRS rules, limits, and forms
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sortedCategories.length > 0 ? (
            <Accordion type="multiple" className="w-full">
              {sortedCategories.map(category => (
                <AccordionItem key={category} value={category}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-stone-900">{category}</span>
                      <Badge variant="secondary" className="bg-[#1e3a5f]/10 text-[#1e3a5f]">
                        {groupedDeductions[category].length}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 gap-3 pt-2">
                      {groupedDeductions[category].map(deduction => (
                        <Dialog key={deduction.id}>
                          <DialogTrigger asChild>
                            <div
                              className="p-4 rounded-lg bg-stone-50 border border-stone-100 hover:border-[#1e3a5f]/30 hover:bg-[#1e3a5f]/5 transition-all cursor-pointer"
                              onClick={() => setSelectedDeduction(deduction)}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h4 className="font-medium text-stone-900">{deduction.name}</h4>
                                  <p className="text-sm text-stone-600 mt-1">{deduction.description}</p>
                                </div>
                                <div className="ml-4 flex flex-col items-end gap-1">
                                  {deduction.limit && (
                                    <Badge variant="outline" className="text-xs whitespace-nowrap">
                                      {deduction.limit.length > 20 ? deduction.limit.substring(0, 20) + "..." : deduction.limit}
                                    </Badge>
                                  )}
                                  {deduction.form && (
                                    <span className="text-xs text-stone-400">{deduction.form.split(",")[0]}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="text-[#1e3a5f]">{deduction.name}</DialogTitle>
                              <DialogDescription>{deduction.description}</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              {/* IRS Rules */}
                              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                                <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                                  </svg>
                                  IRS Rules & Requirements
                                </h4>
                                <p className="text-sm text-blue-700 leading-relaxed">{deduction.irs_rules}</p>
                              </div>

                              {/* Limit */}
                              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                                <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                                  </svg>
                                  Deduction Limit
                                </h4>
                                <p className="text-sm text-amber-700 font-medium">{deduction.limit || "No specific limit"}</p>
                              </div>

                              {/* Form */}
                              <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                                <h4 className="font-semibold text-purple-800 flex items-center gap-2 mb-2">
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                  </svg>
                                  IRS Form(s)
                                </h4>
                                <p className="text-sm text-purple-700 font-medium">{deduction.form || "Schedule C"}</p>
                              </div>

                              {/* Category */}
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-stone-500">Category:</span>
                                <Badge className="bg-[#1e3a5f]/10 text-[#1e3a5f]">{deduction.category}</Badge>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No deductions found matching your search.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* IRS Tips Card */}
      <Card className="bg-gradient-to-br from-blue-50 to-stone-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-800 flex items-center gap-2">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
            IRS Documentation Requirements
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="font-semibold text-blue-900">Required Records</h4>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex gap-2">
                  <span className="text-[#1e3a5f]">✓</span>
                  <span><strong>Receipts</strong> - Keep for all purchases over $75</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#1e3a5f]">✓</span>
                  <span><strong>Mileage Log</strong> - Date, destination, business purpose, miles</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#1e3a5f]">✓</span>
                  <span><strong>Meal Records</strong> - Who, what, where, when, why, amount</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-[#1e3a5f]">✓</span>
                  <span><strong>Home Office</strong> - Square footage, exclusive use proof</span>
                </li>
              </ul>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold text-blue-900">Key IRS Forms</h4>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex gap-2">
                  <span className="font-mono bg-blue-100 px-2 rounded">Schedule C</span>
                  <span>Main business income/expense form</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono bg-blue-100 px-2 rounded">Form 4562</span>
                  <span>Depreciation & Section 179</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono bg-blue-100 px-2 rounded">Form 8829</span>
                  <span>Home office deduction</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-mono bg-blue-100 px-2 rounded">1099-NEC</span>
                  <span>Contractor payments $600+</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-4 p-4 rounded-lg bg-amber-100 text-sm text-amber-800">
            <strong>Important:</strong> Keep records for at least 3 years (7 years recommended). 
            The IRS can audit returns up to 3 years back, or 6 years if substantial underreporting is suspected.
            Tax laws change annually - consult a CPA for current guidance.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeductionsLibrary;
